import {
  EntitySearchReferenceEntity,
  WbsItemAdditionalPropertyType,
} from '../../../domain/entity/WbsItemAdditionalPropertyEntity'
import { intl } from '../../../i18n'

// Data difinition of a reference entity type is differs between UI and Server (DB)
// so, here we define the reference entity for UI.

export enum EntitySearchReferenceEntityType {
  WBS_ITEM = 'WBS_ITEM',
  TICKET = EntitySearchReferenceEntity.TICKET,
  TEAM = EntitySearchReferenceEntity.TEAM,
  PROJECT_MEMBER = EntitySearchReferenceEntity.PROJECT_MEMBER,
}

export enum EntitySearchReferenceEntityWbsItemType {
  PROCESS = EntitySearchReferenceEntity.PROCESS,
  DELIVERABLE_LIST = EntitySearchReferenceEntity.DELIVERABLE_LIST,
  DELIVERABLE = EntitySearchReferenceEntity.DELIVERABLE,
  TASK = EntitySearchReferenceEntity.TASK,
}

// Entity Type(UI) → Entity(Server)
export const entitySearchTypeToEntity = (
  entityType: EntitySearchReferenceEntityType | undefined,
  entityWbsItemType: EntitySearchReferenceEntityWbsItemType | string | undefined
):
  | {
      entity: EntitySearchReferenceEntity | undefined
      ticketType: string | undefined
    }
  | undefined => {
  switch (entityType) {
    case EntitySearchReferenceEntityType.WBS_ITEM:
      return isEntitySearchReferenceEntity(entityWbsItemType)
        ? {
            entity: entityWbsItemType,
            ticketType: undefined,
          }
        : undefined
    case EntitySearchReferenceEntityType.TICKET:
      return {
        entity: EntitySearchReferenceEntity.TICKET,
        ticketType: entityWbsItemType,
      }
    case EntitySearchReferenceEntityType.TEAM:
    case EntitySearchReferenceEntityType.PROJECT_MEMBER: {
      const strEntityType = entityType.toString()
      return isEntitySearchReferenceEntity(strEntityType)
        ? {
            entity: strEntityType,
            ticketType: undefined,
          }
        : undefined
    }
  }
}

// Entity(Server) → Entity Type(UI)
export const entitySearchEntityToEntityType = (
  entity: EntitySearchReferenceEntity | undefined,
  ticketType: string | undefined
):
  | {
      entityType: EntitySearchReferenceEntityType | undefined
      wbsItemType: EntitySearchReferenceEntityWbsItemType | string | undefined
    }
  | undefined => {
  switch (entity) {
    case EntitySearchReferenceEntity.PROCESS:
    case EntitySearchReferenceEntity.DELIVERABLE_LIST:
    case EntitySearchReferenceEntity.DELIVERABLE:
    case EntitySearchReferenceEntity.TASK: {
      const strEntity = entity.toString()
      return {
        entityType: EntitySearchReferenceEntityType.WBS_ITEM,
        wbsItemType: isEntitySearchReferenceEntityWbsItemType(strEntity)
          ? strEntity
          : undefined,
      }
    }
    case EntitySearchReferenceEntity.TICKET: {
      return {
        entityType: EntitySearchReferenceEntityType.TICKET,
        wbsItemType: ticketType,
      }
    }
    case EntitySearchReferenceEntity.TEAM:
    case EntitySearchReferenceEntity.PROJECT_MEMBER: {
      const strEntity = entity.toString()
      return {
        entityType: isEntitySearchReferenceEntityType(strEntity)
          ? strEntity
          : undefined,
        wbsItemType: undefined,
      }
    }
  }
}

export const getEntitySearchEntityTypeLabel = (
  value: EntitySearchReferenceEntityType
) => {
  switch (value) {
    case EntitySearchReferenceEntityType.WBS_ITEM:
      return intl.formatMessage({
        id: 'wbsItemAdditionalProperties.entitySearchReferenceEntityType.wbs',
      })
    case EntitySearchReferenceEntityType.TICKET:
      return intl.formatMessage({
        id: 'wbsItemAdditionalProperties.entitySearchReferenceEntityType.ticket',
      })
    case EntitySearchReferenceEntityType.TEAM:
      return intl.formatMessage({
        id: 'wbsItemAdditionalProperties.entitySearchReferenceEntityType.team',
      })
    case EntitySearchReferenceEntityType.PROJECT_MEMBER:
      return intl.formatMessage({
        id: 'wbsItemAdditionalProperties.entitySearchReferenceEntityType.projectMember',
      })
    default:
      return ''
  }
}

export const getEntitySearchEntityWbsItemTypeLabel = (
  value: EntitySearchReferenceEntityWbsItemType
) => {
  switch (value) {
    case EntitySearchReferenceEntityWbsItemType.PROCESS:
      return intl.formatMessage({
        id: 'wbsItemAdditionalProperties.entitySearchReferenceEntityType.process',
      })
    case EntitySearchReferenceEntityWbsItemType.DELIVERABLE_LIST:
      return intl.formatMessage({
        id: 'wbsItemAdditionalProperties.entitySearchReferenceEntityType.deliverableList',
      })
    case EntitySearchReferenceEntityWbsItemType.DELIVERABLE:
      return intl.formatMessage({
        id: 'wbsItemAdditionalProperties.entitySearchReferenceEntityType.deliverable',
      })
    case EntitySearchReferenceEntityWbsItemType.TASK:
      return intl.formatMessage({
        id: 'wbsItemAdditionalProperties.entitySearchReferenceEntityType.task',
      })
    default:
      return ''
  }
}

export const shouldHaveEntitySearchEntityWbsItemType = (
  propertyType: WbsItemAdditionalPropertyType,
  entitySearchReferenceEntityType?: EntitySearchReferenceEntityType
): boolean => {
  return (
    WbsItemAdditionalPropertyType.ENTITY_SEARCH === propertyType &&
    (EntitySearchReferenceEntityType.WBS_ITEM ===
      entitySearchReferenceEntityType ||
      EntitySearchReferenceEntityType.TICKET ===
        entitySearchReferenceEntityType)
  )
}

const isEntitySearchReferenceEntity = (
  val: EntitySearchReferenceEntityWbsItemType | string | undefined
): val is EntitySearchReferenceEntity =>
  Object.values(EntitySearchReferenceEntity).some(
    entity => entity.toString() === val
  )

const isEntitySearchReferenceEntityType = (
  val: string
): val is EntitySearchReferenceEntityType =>
  Object.values(EntitySearchReferenceEntityType).some(
    entityType => entityType.toString() === val
  )

export const isEntitySearchReferenceEntityWbsItemType = (
  val: EntitySearchReferenceEntityWbsItemType | string | undefined
): val is EntitySearchReferenceEntityWbsItemType =>
  Object.values(EntitySearchReferenceEntityWbsItemType).some(
    wbsItemType => wbsItemType.toString() === val
  )
